import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { simulateCrash } from '../../api/telematics';

const SIDES = ['front', 'front_right', 'right', 'rear_right', 'rear', 'rear_left', 'left', 'front_left'];
const SEVERITIES = ['minor', 'moderate', 'severe'];

// Admin demo tool: fires a synthetic directional crash through the real analyzer for a
// vehicle with an active device. The resulting CrashEvent is flagged simulated.
export default function SimulateCrashModal({ open, onClose, devices }) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const linked = (devices || []).filter((d) => d.status === 'active' && d.vehicle && d.vehicle._id);
  const [vehicleId, setVehicleId] = useState('');
  const [side, setSide] = useState('front_left');
  const [severity, setSeverity] = useState('moderate');
  const [state, setState] = useState('idle'); // idle | sending | sent | error

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!vehicleId) return;
    setState('sending');
    try {
      await simulateCrash({ vehicleId, side, severity }, token);
      setState('sent');
    } catch (err) {
      setState('error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={submit} className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl space-y-3 text-gray-900">
        <h2 className="text-lg font-semibold">{t('telematics.simulate.title')}</h2>
        <p className="text-xs text-gray-500">{t('telematics.simulate.note')}</p>

        <label className="block text-sm">{t('telematics.simulate.vehicle')}
          <select className="mt-1 w-full rounded border px-3 py-2" value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)} required>
            <option value="">—</option>
            {linked.map((d) => (
              <option key={d._id} value={d.vehicle._id}>
                {d.vehicle.plateNumber} — {d.vehicle.brand || ''} {d.vehicle.model || ''}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">{t('telematics.simulate.side')}
          <select className="mt-1 w-full rounded border px-3 py-2" value={side} onChange={(e) => setSide(e.target.value)}>
            {SIDES.map((s) => <option key={s} value={s}>{t(`ownerTelematics.crashAlert.sectors.${s}`)}</option>)}
          </select>
        </label>

        <label className="block text-sm">{t('telematics.simulate.severity')}
          <select className="mt-1 w-full rounded border px-3 py-2" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            {SEVERITIES.map((s) => <option key={s} value={s}>{t(`ownerTelematics.crashAlert.severity.${s}`)}</option>)}
          </select>
        </label>

        {linked.length === 0 && <p className="text-sm text-amber-700">{t('telematics.simulate.noVehicles')}</p>}
        {state === 'sent' && <p className="text-sm text-green-600">{t('telematics.simulate.success')}</p>}
        {state === 'error' && <p className="text-sm text-red-600">{t('telematics.simulate.error')}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded px-4 py-2 text-sm">{t('common.close')}</button>
          <button type="submit" disabled={state === 'sending' || !vehicleId}
            className="rounded bg-red-600 px-4 py-2 text-sm text-white disabled:opacity-50">
            {state === 'sending' ? t('common.loading') : t('telematics.simulate.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}
